import { useState } from "react";
import Draggable from "react-draggable";
import NumberFormat from "react-number-format";
import Portal from "../../common/components/Portal";
import ConfirmationModal from "../../common/components/ConfirmationModal";

export const BulkDefaultsModal = ({
    onCancel,
    onSave,
    initialValues = {},
    dynamicFields = [],
    isSubmitting = false,
    disableInflationInputs = false,
    settingsFieldDisabled
}) => {
    const [values, setValues] = useState({
        initialInflation: initialValues.initialInflation ?? null,
        escalation: initialValues.escalation ?? null,
        contingency: initialValues.contingency ?? null,
        dynamic_fields: dynamicFields.reduce((acc, f) => {
            acc[f.key] = initialValues.dynamic_fields?.[f.key] ?? null;
            return acc;
        }, {})
    });
    const [modifiedFields, setModifiedFields] = useState({});
    const [showConfirmModal, setConfirmationModal] = useState(false);

    const handleChange = (key, val) => {
        setValues(v => ({ ...v, [key]: val ?? "" }));
        setModifiedFields(v => ({ ...v, [key]: true }));
    };
    const handleDynChange = (key, val) => {
        setValues(v => ({ ...v, dynamic_fields: { ...v.dynamic_fields, [key]: val ?? "" } }));
        setModifiedFields(v => ({ ...v, dynamic_fields: { ...(v.dynamic_fields || {}), [key]: true } }));
    };

    const hasModified =
        modifiedFields.initialInflation ||
        modifiedFields.escalation ||
        modifiedFields.contingency ||
        (modifiedFields.dynamic_fields && Object.keys(modifiedFields.dynamic_fields).length > 0);

    const handleCancel = () => {
        if (hasModified) {
            setConfirmationModal(true);
        } else {
            onCancel();
        }
    };

    const renderConfirmationModal = () => {
        if (!showConfirmModal) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"Do you want to discard all changes?"}
                        message={"This action cannot be reverted, are you sure that you need to discard changes?"}
                        onNo={() => setConfirmationModal(false)}
                        onYes={() => {
                            setConfirmationModal(false);
                            onCancel();
                        }}
                    />
                }
                onCancel={() => setConfirmationModal(false)}
            />
        );
    };

    const submitDefaults = () => {
        const payload = {};
        if (modifiedFields.initialInflation) payload.initialInflation = values.initialInflation;
        if (modifiedFields.escalation) payload.escalation = values.escalation;
        if (modifiedFields.contingency) payload.contingency = values.contingency;
        if (modifiedFields.dynamic_fields) {
            payload.dynamic_fields = {};
            Object.keys(modifiedFields.dynamic_fields).forEach(key => {
                payload.dynamic_fields[key] = values.dynamic_fields[key];
            });
        }
        onSave(payload);
    };

    return (
        <div className="modal modal-region modal-view inbox-modal" id="bulkDefaultsModal" tabIndex="-1" style={{ display: "block" }}>
            <Draggable handle=".draggable" cancel=".non-draggable">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header draggable">
                            <h5 className="modal-title">
                                <div className="txt-hed">Default {settingsFieldDisabled ? "Actual" : "Settings"}</div>
                            </h5>
                            <button type="button" className="close non-draggable" onClick={handleCancel}>
                                <span aria-hidden="true">
                                    <img src="/img/close.svg" alt="Close" />
                                </span>
                            </button>
                        </div>
                        <div className="modal-body region-otr sm-chart-upload">
                            <div className="col-md-12 main-sec soft-cost-default overflow-auto">
                                <div className="form-row">
                                    {dynamicFields.map(field => (
                                        <div className="form-group col-12 mb-4" key={field.key}>
                                            <label>{field.name} (%)</label>
                                            <NumberFormat
                                                className="form-control"
                                                displayType="input"
                                                value={values.dynamic_fields[field.key]}
                                                onValueChange={vals => handleDynChange(field.key, vals.floatValue)}
                                                suffix=" %"
                                                decimalScale={3}
                                                style={
                                                    modifiedFields.dynamic_fields?.[field.key]
                                                        ? { backgroundColor: "#FF000020", border: "1px solid red" }
                                                        : {}
                                                }
                                            />
                                        </div>
                                    ))}
                                    <div className="form-group col-12 mb-4">
                                        <label>Escalation (%)</label>
                                        <NumberFormat
                                            className="form-control"
                                            displayType="input"
                                            value={values.escalation}
                                            onValueChange={vals => handleChange("escalation", vals.floatValue)}
                                            suffix=" %"
                                            decimalScale={3}
                                            style={modifiedFields.escalation ? { backgroundColor: "#FF000020", border: "1px solid red" } : {}}
                                        />
                                    </div>
                                    <div className="form-group col-12 mb-4">
                                        <label>Contingency (%)</label>
                                        <NumberFormat
                                            className="form-control"
                                            displayType="input"
                                            value={values.contingency}
                                            onValueChange={vals => handleChange("contingency", vals.floatValue)}
                                            suffix=" %"
                                            decimalScale={3}
                                            style={modifiedFields.contingency ? { backgroundColor: "#FF000020", border: "1px solid red" } : {}}
                                        />
                                    </div>
                                    <div className="form-group col-12 mb-4">
                                        <label>Initial Inflation (%)</label>
                                        <NumberFormat
                                            className={`form-control ${disableInflationInputs ? "cursor-disabled" : ""}`}
                                            displayType="input"
                                            value={values.initialInflation}
                                            onValueChange={vals => handleChange("initialInflation", vals.floatValue)}
                                            suffix=" %"
                                            decimalScale={3}
                                            disabled={disableInflationInputs}
                                            style={modifiedFields.initialInflation ? { backgroundColor: "#FF000020", border: "1px solid red" } : {}}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="btn-sec text-center">
                                {hasModified && (
                                    <p className="text-danger mb-2" style={{ fontSize: "14px", fontWeight: "500" }}>
                                        * Only modified fields (highlighted in red) will be applied to project costs
                                    </p>
                                )}
                                <div className="text-right btnOtr edit-cmn-btn">
                                    <button
                                        type="button"
                                        className="btn btn-create save"
                                        disabled={isSubmitting || !hasModified}
                                        onClick={submitDefaults}
                                    >
                                        Save{isSubmitting && <span className="spinner-border spinner-border-sm pl-2" role="status"></span>}
                                    </button>
                                    <button type="button" className="btn btn-secondary btnClr mr-1" disabled={isSubmitting} onClick={handleCancel}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                            {renderConfirmationModal()}
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>
    );
};
